/**
 * The gradebook. Everything day-to-day happens here: marks and students. Reads
 * and writes go through `@/gradebook/api`.
 *
 * A cell holds ONE thing: a raw score out of its column's max, or a status
 * (`M` / `Ex`). There is no Raw / % display mode - see `display.js` for what
 * that toggle cost. The cell shows the numerator alone; the denominator belongs
 * to the column, which has one for all of it.
 *
 * K/T/C/A SPLITS. An assessment may be marked out of one number, or split across
 * Ontario's four achievement categories. A split is not a different KIND of
 * column - it is the same column with its sections shown side by side, each with
 * its own denominator, summing to the assessment's `max`. `sectionsOf()` in
 * `grading.js` is the single test for which shape a column is; there is no
 * `isSplit` flag here or anywhere else.
 *
 * ONE VIEW CONTROL, NOT TWO. "Compact" is the only switch, and a split always
 * shows its sections in BOTH positions of it:
 *
 *     COMPACT   -> sections shown, derived TOTAL column dropped, weight row hidden.
 *     EXPANDED  -> sections shown, total column added back, weight row shown.
 *
 * An earlier design had compact roll a split up into a single total cell. It was
 * abandoned because it hid the marks behind a view change: a teacher could not
 * type into a section without switching mode first, and clicking a cell threw
 * the whole sheet into the other mode. NO CELL IN THIS GRID SWITCHES MODES.
 *
 * The Grade column is CALCULATED and read-only - a weighted average of the marks
 * currently in view. Nothing about it is stored, and a teacher cannot type over
 * it: the gradebook reports what the marks say, and professional judgement is
 * applied on the report card rather than written back into the marks record.
 *
 * Styled as a spreadsheet, not a dashboard. Colour carries exactly one meaning -
 * green means a value is present - so the sheet reads as a completion check
 * rather than a heatmap. The sheet is otherwise FLAT: the alternating row and
 * column banding is gone, and the crosshair on the cursor's row and column is
 * the only structural cue left. A static grey lattice under every cell competed
 * with the two things that actually carry meaning - where the cursor is, and
 * whether a cell holds a value.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, Plus, SlidersHorizontal, Trash2, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PageContainer } from "@/layouts/PageContainer";
import {
  CELL_ENTERED, CROSSHAIR_BOTH, CROSSHAIR_COL, CROSSHAIR_ROW,
  SECTIONS, SECTION_MAX_FIELD, SECTION_SCORE_FIELD,
  assessmentsInRange, defaultRange, formatDate, formatDateLong, markPercent,
  marked, percentToLetter, sectionsOf, weightedAverage,
} from "@/gradebook/grading";
import { effectiveWeights, toWeightRows } from "@/gradebook/categories";
import NewAssessmentModal from "@/components/modals/NewAssessmentModal";
import NewStudentModal from "@/components/modals/NewStudentModal";
import { DeleteStudentModal } from "@/components/modals/GradebookModals";
import { formatMark, markWarning, parseMark } from "@/gradebook/display";
import { DateRangeFilter } from "@/gradebook/DateRangeFilter";
import { titleColorClass } from "@/gradebook/titleColors";
import { schoolYearRange } from "@/gradebook/schoolYear";
import { useSyncQueue } from "@/gradebook/useSyncQueue";
import { SyncIndicator } from "@/gradebook/SyncIndicator";
import { useGradebookApi } from "@/gradebook/api";
import { useGradebookData } from "@/gradebook/useGradebookData";

/* ── Geometry ─────────────────────────────────────────────────────────────── */

/**
 * The roster column is sized in CHARACTERS, not pixels — but a name and an OEN
 * need DIFFERENT units, and that is the whole subtlety here.
 *
 * `ch` is the width of a "0", which is about twice an average lowercase letter
 * in this face: a 20ch box held roughly FORTY characters, so the column came out
 * twice the width asked for and no name in the roster ever clipped. A name is
 * proportional text, so it is sized in measured pixels-per-average-glyph.
 *
 * Either way the column is FIXED. A long name clips to an ellipsis rather than
 * widening the column, so one "Novak-Petrovic-Kowalczyk" cannot shove every mark
 * on the sheet sideways. Full name on hover.
 *
 * WHY THESE ARE PIXELS ON THE CONTENT AND NOT ON THE CELL. Both were tried.
 * Under `table-layout: auto` a cell's `width` is only a SUGGESTION and
 * `min-width` is a floor, never a ceiling — the browser still grows the column
 * to fit its longest content, so the roster measured 297px against a declared
 * 147px and no name ever clipped. The cell cannot be constrained; the CONTENT
 * has to be, so the name and OEN fields each carry a hard width and the column
 * has nothing left to grow around.
 */
const NAME_CHARS = 20;
const SHEET_TEXT_PX = 14;
/* Average lowercase glyph at this size, measured — not the "0" that `ch` uses,
 * which is about twice as wide and was the source of the 40-character column. */
const PX_PER_CHAR = 7.7;
const W_NAME_TEXT = Math.round(NAME_CHARS * PX_PER_CHAR);

/**
 * The OEN box is deliberately OVER-BUDGETED — wider than nine digits strictly
 * need — and the number sits CENTRED in it.
 *
 * Nothing cleverer is required, and two cleverer things were tried and thrown
 * away. A monospace face made every glyph uniform but set proper names like
 * code (and this app has no `font-mono` utility at all: `tailwind.config.js`
 * sets `theme.fontFamily` rather than `theme.extend.fontFamily`, replacing the
 * scale). Then 3-3-3 grouping spread edge to edge across the box: the gaps were
 * too wide to read as one number.
 *
 * `tabular-nums` DOES NOT WORK HERE and must not be relied on anywhere in this
 * app. The property is applied and computes correctly — `font-variant-numeric:
 * tabular-nums` is right there in the computed style — but DM Sans as loaded
 * carries no `tnum` table, so it is inert. Measured at 13px: "1" is 4.06px and
 * "0" is 8.89px, a 4.8px spread across a 9-digit number. That is the ragged
 * column. So each digit gets its OWN fixed box (`DIGIT_W`) and is centred in it:
 * font independent, needs no OpenType feature, and cannot quietly stop working
 * when the typeface changes.
 */
const W_OEN_TEXT = 88;
/** Wider than the widest digit (measured: "0" at 0.684em), so nothing overflows
 *  its box and every OEN is exactly 9 x this. */
const DIGIT_W = "0.7em";
const W_TERM = 100;
/** The per-row delete rail, OUTSIDE the Grade column at the far right edge -
 *  destruction sits past everything it would destroy, not beside the name. */
const W_DEL = 28;
/* 130, not 120. Inherited from the diagonal headers this replaced, where a
 * label's vertical run was `(textWidth + lineHeight) / sqrt(2)` and 120 clipped
 * the longest titles through the top of the scroll container. Vertical text
 * needs the room just as much, and the expanded header's flexible title band
 * absorbs it, so nothing else moves. */
const H_HEAD = 130;

/** Condensed is the working width - a full year of columns with no horizontal
 *  scroll. Expanded trades that away to show each name in full. */
const W_MARK_CONDENSED = 34;
const W_MARK_EXPANDED = 116;

/** Height of the compact header's foot strip. A split shows its K/T/C/A letters
 *  here; a single column leaves it EMPTY, so every vertical title above it
 *  starts at the same height instead of stepping around whichever columns have
 *  letters beneath them. */
const H_STRIP = 20;

/* A sub-column only ever holds two or three digits and a one-letter header, so
 * it does NOT follow the sheet's width toggle. That toggle exists to fit
 * assessment NAMES; a section has no name to fit, and four of them at the
 * expanded 116px would be 464px for a single test. */
const W_PART = 40;
/* Compact runs the section columns tighter still. A two-digit mark is ~18px, so
 * 40px left ~11px of air each side; 30 halves that. Three digits still fit. */
const W_PART_COMPACT = 30;
const W_TOTAL = 48;

/* The gutter before Grade absorbs the table's leftover width. The table is
 * `w-full`, so without one column left free every declared width gets stretched
 * and a 40px section renders at 50.
 *
 * 48 in BOTH modes now. Compact used to reserve `H_HEAD + 48` because its labels
 * ran DIAGONALLY and the last one leaned a full header-height to the right, so
 * the gutter had to clear that lean or the "+" sat on top of it. Compact labels
 * are vertical, which lean over nothing, so the extra 130px was pure dead space
 * pushing Grade off the side of a scrolling sheet. */
const W_PLUS = 48;

/** Wide enough for a full assessment title on one line. */
const POPOVER_W = 280;

/**
 * The sheet's vertical column rule.
 *
 * Deliberately darker than the app's `border-border`. That token is tuned for
 * separating cards and panels, where one line divides two large areas; here it
 * has to divide a 40px column from its neighbour forty times across a screen,
 * and at that density it washed out — the grid read as a field of numbers with
 * no columns in it.
 */
const COL_RULE = "border-r-muted-foreground/50";

/**
 * Header fill: lighter than `bg-muted`, but OPAQUE.
 *
 * `bg-muted/50` gave the right tone and broke the sheet — the roster and Grade
 * headers are sticky, so at 50% alpha the titles of the columns scrolling
 * underneath showed straight through them.
 *
 * AN ALPHA CANNOT BE USED ON ANYTHING STICKY. This layers the same 50% muted
 * wash as a background-IMAGE over an opaque `bg-card`, which composites to the
 * identical colour while stopping everything behind it. Theme-safe: both halves
 * are tokens, so dark mode follows.
 */
const HEAD_FILL =
  "bg-card bg-[linear-gradient(hsl(var(--muted)/0.5),hsl(var(--muted)/0.5))]";

/**
 * Excel-ish: no radius, gridlines come from the cell, thick outline on focus.
 *
 * No `tabular-nums`, here or on any other number in this sheet. It is INERT in
 * this typeface (see `DIGIT_W`), and leaving it scattered around implies an
 * alignment guarantee nothing is actually providing — the next person to line a
 * column up would trust it and lose an hour. Where digits must align, they get a
 * fixed box.
 */
const INPUT = [
  "block h-6 w-full border-0 bg-transparent text-center text-[14px] text-foreground",
  "focus:[outline:2px_solid_hsl(var(--primary))] focus:[outline-offset:-2px]",
].join(" ");

/** Spelt out for the letter tooltips. The grid shows one glyph per section, and
 *  a teacher who has not met the achievement chart in a while needs the word. */
const SECTION_NAME = {
  K: "Knowledge and Understanding",
  T: "Thinking",
  C: "Communication",
  A: "Application",
};

/**
 * TWO statuses, not three.
 *
 * `absent` was dropped: arithmetically it did exactly what `excused` does — drop
 * out of the weighted average — so the sheet carried two words for one
 * behaviour and a teacher had to guess which to reach for. `excused` is the
 * survivor because it covers both readings; an away student IS excused from this
 * evaluation, while an IEP exemption is in no sense "absent". If "who still owes
 * me this test" ever becomes a feature, that is precisely what `absent` was for
 * and it comes back as one more enum value.
 */
const STATUS_CHOICES = [
  ["graded", "Clear status"],
  ["missing", "Missing (counts as 0)"],
  ["excused", "Excused (not counted)"],
];

/** An empty cell, ready to take one section's score without disturbing the
 *  others. Every field is spelt out because `api.saveMark` sends the WHOLE cell
 *  and an absent key would be written as null. */
const BLANK_MARK = {
  status: "graded", score: null, scoreK: null, scoreT: null, scoreC: null, scoreA: null,
};

/** A status replaces the whole cell, sections and all — a student who was away
 *  missed the test, not its Thinking section. */
const statusMark = (status) => ({ ...BLANK_MARK, status });

/**
 * Section counterpart of `markWarning`.
 *
 * Same rule, and it is worth restating because it looks like a validation gap:
 * a score above the max is SAVED, not refused — 12/10 on a section is a real
 * bonus mark. It is also exactly what a typo looks like, and the denominator
 * lives up in the header, so it is called out rather than silently accepted.
 */
const sectionWarning = (value, max) => (
  value != null && Number(max) > 0 && value > Number(max) ? `Mark out of ${Number(max)}` : null
);

/**
 * An editable roster cell — a student's name or OEN.
 *
 * Same shape as a mark cell and for the same reason: it holds a DRAFT of exactly
 * what was typed and only commits on blur or Enter. Binding straight to the
 * stored value re-renders mid-keystroke, which is what made marks fight the
 * typist before the draft existed.
 *
 * Escape abandons the edit. An empty NAME is refused on commit (a nameless row
 * is not a student), while an empty OEN is meaningful — it clears one — so the
 * caller decides via `onCommit`.
 */
function StudentField({ value, placeholder, onCommit, className, style, ariaLabel }) {
  const [draft, setDraft] = useState(null);
  const text = draft ?? value;

  const commit = () => {
    if (draft === null) return;
    setDraft(null);
    if (draft !== value) onCommit(draft);
  };

  return (
    <input
      value={text}
      placeholder={placeholder}
      aria-label={ariaLabel}
      spellCheck={false}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => { setDraft(value); e.target.select(); }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Escape") { setDraft(null); e.currentTarget.blur(); return; }
        if (e.key === "Enter") e.currentTarget.blur(); // blur commits
      }}
      style={style}
      className={cn(
        "h-6 truncate border border-transparent bg-transparent px-1 text-[13px]",
        "outline-none transition-colors hover:border-border",
        "focus:border-primary focus:bg-background",
        "placeholder:text-muted-foreground/50",
        className,
      )}
    />
  );
}

/**
 * The OEN, editable, but rendered as fixed-width digit boxes when it is not
 * being edited.
 *
 * These two requirements fight each other and this is the cheapest truce. The
 * per-digit boxes are the only thing that makes a column of OENs line up (see
 * `DIGIT_W` — `tabular-nums` is inert in this typeface), and an `<input>` cannot
 * put its own glyphs in boxes. So the input stays mounted and keeps every bit of
 * its editing behaviour — tab order, caret, draft, commit-on-blur — and an
 * OPAQUE overlay sits on top of it while it is unfocused, showing the same
 * digits properly spaced.
 *
 * Rejected: swapping the input out for a span and back on click. It works until
 * you reach the cell by keyboard, at which point there is nothing to focus.
 *
 * The overlay is drawn only when there IS an OEN, so an empty cell still shows
 * its placeholder — that placeholder is the entire affordance saying an OEN can
 * be typed here, and plenty of students genuinely have none.
 */
function OenCell({ student, onCommit }) {
  const [editing, setEditing] = useState(false);
  return (
    <span
      className="relative block shrink-0"
      style={{ width: W_OEN_TEXT }}
      onFocusCapture={() => setEditing(true)}
      onBlurCapture={() => setEditing(false)}
    >
      <StudentField
        value={student.oen ?? ""}
        placeholder="OEN"
        onCommit={onCommit}
        className="w-full text-center text-muted-foreground"
        ariaLabel={`OEN for ${student.name}`}
      />
      {!editing && student.oen && (
        <span
          aria-hidden
          title={`OEN ${student.oen}`}
          className="pointer-events-none absolute inset-0 flex items-center justify-center bg-card text-[13px] text-muted-foreground"
        >
          {[...student.oen].map((digit, i) => (
            <span key={i} style={{ width: DIGIT_W }} className="inline-block text-center">
              {digit}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

/**
 * Statuses are CHOSEN, not typed.
 *
 * Typing them was the old design and it cost a real bug — `A` had to mean
 * Absent, which meant `A` could never mean anything else — and it left the
 * statuses undiscoverable to anyone who had not read a tooltip.
 *
 * On a split this always writes the WHOLE cell: a student who was away did not
 * miss the Thinking section, they missed the test. That is why status stays
 * one-per-cell in the schema rather than one-per-section.
 */
function StatusMenu({ at, onPick, onClose }) {
  if (!at) return null;
  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
      />
      <div
        className="fixed z-50 min-w-[9rem] border border-border bg-card py-1 shadow-card"
        style={{ left: at.x, top: at.y }}
      >
        {STATUS_CHOICES.map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => { onPick(value); onClose(); }}
            className="block w-full px-3 py-1.5 text-left text-[12px] text-foreground transition-colors hover:bg-muted"
          >
            {label}
          </button>
        ))}
      </div>
    </>
  );
}

/**
 * Touch equivalent of a right-click. THIS IS THE ENTIRE COST of not stranding
 * the iPads a lot of teachers mark from — a timer and three handlers, no gesture
 * library.
 *
 * 500ms matches the platform convention. A move cancels, so a long-press never
 * fires while the teacher is scrolling the sheet, which is the one way a naive
 * implementation goes wrong.
 */
function useLongPress(ms = 500) {
  const timer = useRef(null);
  const stop = () => { clearTimeout(timer.current); timer.current = null; };
  useEffect(() => stop, []);
  /* Called once, then used as a props factory per cell — only one press can be
   * in flight at a time, so one shared timer is all it needs. */
  return (onFire) => ({
    onTouchStart: (e) => {
      const { clientX: x, clientY: y } = e.touches[0];
      timer.current = setTimeout(() => onFire({ x, y }), ms);
    },
    onTouchMove: stop,
    onTouchEnd: stop,
    onTouchCancel: stop,
  });
}

/** The View options dropdown's persisted state - see `toggleView` in the
 *  sheet. App-wide, not per-gradebook: the preference is about how this
 *  teacher likes to read a sheet, not about one class. */
const VIEW_STORE = "easyreports.gradebook.view";
const VIEW_DEFAULTS = {
  compact: false,
  showOen: false,
  showWeights: false,
  showAverages: false,
  showGrade: false,
};

function loadViewOptions() {
  try {
    return { ...VIEW_DEFAULTS, ...JSON.parse(localStorage.getItem(VIEW_STORE) ?? "{}") };
  } catch {
    return { ...VIEW_DEFAULTS }; // private mode, corrupted value - defaults win
  }
}

/* The gradebook now arrives from `GradebookLayout`, which owns it for the whole
   `/gradebooks/:id` section. That is what stops a hop to Insights and back from
   re-downloading every mark in the book. The layout has already dealt with
   loading and failure, so `gradebook` here is never null and this component has
   no spinner or not-found branch of its own. */
export default function Gradebook() {
  const { gradebook, refetch, mutate } = useGradebookData();

  /* Keyed on the id so switching gradebooks remounts the sheet rather than
   * leaving one book's marks seeded into another's state. */
  return (
    <GradebookSheet
      key={gradebook.id}
      gradebook={gradebook}
      onReload={refetch}
      onMutate={mutate}
    />
  );
}

function GradebookSheet({ gradebook, onReload, onMutate }) {
  const api = useGradebookApi();
  /* Every mark write goes through here rather than straight at the API: it
   * collapses a burst of keystrokes into one request per cell, keeps two writes
   * for the same cell from racing, retries, and turns a failure into something
   * the teacher can see instead of a console line. */
  const sync = useSyncQueue();

  /**
   * The range the sheet opens on: the span of the assessments that actually
   * exist, so EVERY column is visible until the teacher narrows it themselves.
   *
   * This deliberately does NOT default to the gradebook's school year. It did,
   * and that hid real data: a book labelled 2025-2026 holding assessments dated
   * September and October 2026 opened with an empty grid, because both fell
   * outside Sep 2025 - Jun 2026. The teacher had entered perfectly sensible
   * dates; the filter simply disagreed with the label.
   *
   * The rule this encodes - the same one `schoolYearRange` follows when it
   * cannot parse a label - is that a filter must never hide data before anyone
   * has touched it. `schoolYear` is only a fallback for a gradebook with no
   * assessments yet, where there is no span to measure and it is a reasonable
   * first window.
   */
  const fullRange = useMemo(
    () => defaultRange(gradebook.assessments, gradebook.schoolYear, schoolYearRange),
    [gradebook.assessments, gradebook.schoolYear],
  );
  const [range, setRange] = useState(() => fullRange);
  /* The sheet owns its marks optimistically - a spreadsheet must never wait on a
   * server to show the number you just typed. Writes go to the API in parallel. */
  const [marks, setMarks] = useState(() => ({ ...gradebook.marks }));
  const [addingStudent, setAddingStudent] = useState(false);
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [cursor, setCursor] = useState({ row: null, col: null });
  /* The right-click status menu: `{ x, y, studentId, a }`, or null. */
  const [menu, setMenu] = useState(null);
  /**
   * Header hover card: `{ id, left, top }`.
   *
   * Compact titles are cut to the header's HEIGHT, so most of them are
   * truncated — the full name has to be one hover away or the sheet is a grid of
   * mystery columns. It carries the things that do not fit in a 13px strip
   * either: the category, the date in full, the weight, and a split's
   * per-section maxes.
   *
   * Positioned FIXED off the header's own rect rather than nested inside the
   * `<th>`: a sticky th is a stacking context sitting under its neighbours, and
   * lifting it to escape would punch its opaque rectangle through the columns
   * either side.
   */
  const [peek, setPeek] = useState(null);
  /* THE VIEW OPTIONS - one object, persisted. Every switch starts OFF (an
   * unchecked menu is legible: whatever is ticked is what this teacher chose),
   * and any change is written to localStorage so the sheet reopens the way
   * they left it. */
  const [view, setView] = useState(loadViewOptions);
  const toggleView = (key) =>
    setView((v) => {
      const next = { ...v, [key]: !v[key] };
      try {
        localStorage.setItem(VIEW_STORE, JSON.stringify(next));
      } catch { /* private mode - the toggle still works for this session */ }
      return next;
    });
  const { showOen, showWeights, showAverages, showGrade } = view;
  const expanded = !view.compact;
  const [adding, setAdding] = useState(false);

  const cells = useRef({});

  /* Measured live by `revealCell` so a keyboard move never parks the cursor
   * behind a frozen edge. Refs rather than the width constants because the
   * roster's width changes with the OEN toggle. */
  const scrollRef = useRef(null);
  const rosterRef = useRef(null);
  const termRef = useRef(null);
  const headRef = useRef(null);
  const sheetRef = useRef(null);
  const longPress = useLongPress();

  /**
   * Did this focus arrive from a mouse? Set on mousedown, which fires first.
   *
   * Clicking a cell must NOT select what is in it. Select-on-focus means a click
   * brings the mark up highlighted, so the next keystroke — or a stray one —
   * replaces a mark the teacher only meant to look at.
   *
   * Arriving by Tab or an arrow key still selects, because that is the marking
   * run: land on a cell and type the new mark straight over the old one. A
   * pointer means "I am reading this"; a key means "I am marking this".
   */
  const viaPointer = useRef(false);

  /**
   * Clicking away from the sheet drops the crosshair.
   *
   * This sheet used to KEEP the cursor after a blur, on the Excel reasoning that
   * a selection survives tabbing away. That holds inside the grid; it does not
   * hold once you click a toolbar or empty page, where a crosshair still burning
   * on a row you have left is just stale ink.
   *
   * mousedown, not click: it fires before focus moves, so the crosshair clears
   * on the way out rather than flickering back for a frame.
   */
  useEffect(() => {
    const onDown = (e) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target)) {
        setCursor({ row: null, col: null });
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const rosterW = W_NAME_TEXT + (showOen ? W_OEN_TEXT + 6 : 0) + 18;
  const W_MARK = expanded ? W_MARK_EXPANDED : W_MARK_CONDENSED;
  const W_PART_NOW = expanded ? W_PART : W_PART_COMPACT;
  /* Hiding the weight band gives its 28px (the h-7 strip) back to the page
   * rather than to the title band - the point of switching it off is vertical
   * space. Compact never had the band, so it keeps the full height for its
   * vertical titles. */
  const headH = expanded && !showWeights ? H_HEAD - 28 : H_HEAD;

  /* Weight belongs to the category, not the assessment: an assessment holds a
   * share of its category and nothing more. No category means "Not counted". */
  const allAssessments = gradebook.assessments;
  const weightOf = useMemo(
    () => effectiveWeights(toWeightRows(allAssessments), gradebook.categories),
    [allAssessments, gradebook.categories],
  );
  const categoryOf = useMemo(
    () => Object.fromEntries(
      toWeightRows(allAssessments)
        .map((r) => [r.id, gradebook.categories.find((c) => c.id === r.categoryId) ?? null]),
    ),
    [allAssessments, gradebook.categories],
  );

  const columns = useMemo(
    () => assessmentsInRange(allAssessments, range.from, range.to)
      .map((a) => ({ ...a, weight: weightOf[a.id] ?? 0 })),
    [allAssessments, range, weightOf],
  );
  const students = gradebook.students;

  /**
   * The flat list of SLOTS the body and the footer iterate. The header iterates
   * `columns` instead and spans with `colSpan`, which is what keeps a split's
   * banner from needing an extra header row that every single-mark column would
   * then have to pay for in permanent height.
   *
   * A split always contributes its sections. What `expanded` adds is the TOTAL:
   * it is derived, it is the widest slot in the group, and its number is
   * recoverable at a glance by switching modes.
   */
  const cols = useMemo(() => {
    const out = [];
    for (const a of columns) {
      const secs = sectionsOf(a);
      if (!secs.length) { out.push({ kind: "single", a }); continue; }
      for (const s of secs) out.push({ kind: "part", a, section: s });
      if (expanded) out.push({ kind: "total", a });
    }
    return out;
  }, [columns, expanded]);

  const marksOf = (studentId) => {
    const out = {};
    for (const a of columns) {
      const m = marks[`${studentId}:${a.id}`];
      if (m) out[a.id] = m;
    }
    return out;
  };

  /**
   * The cell currently being typed in: `{ key, text }`, or null.
   *
   * WHY A DRAFT AND NOT A DIRECTLY-BOUND VALUE: the input used to render
   * `formatMark(mark)`, which meant every keystroke was parsed, stored and
   * REFORMATTED back into the box mid-type. Typing `85` in the old % mode went
   * `8` -> stored 8 -> redrawn as `8%` with the caret pushed past the `%` ->
   * `8%5`, and a third digit produced `85%0`, which parsed to 850, failed the
   * 0-100 check, and was silently discarded - so the box sat there showing a
   * number the teacher had not typed and would not accept another keystroke.
   *
   * DO NOT re-bind `value` to `formatMark`. The % mode that produced the worst
   * of that is gone, but the mechanism is not mode-specific: `3.5` still loses
   * its `.` the instant it is typed if every keystroke round-trips through the
   * parser (`3.` parses to 3 and redraws as `3`).
   *
   * On a split the key is `studentId:assessmentId:SECTION`, because four cells
   * of one row are editable independently. The SYNC key stays the cell
   * (`studentId:assessmentId`) — see `writeCell`.
   */
  const [draft, setDraft] = useState(null);

  /**
   * Persist one cell, whole.
   *
   * `parseMark` returns `{ status, score }` and knows nothing about splits — the
   * number a teacher types is the same number either way. It is THIS function's
   * job to land that score on the right field: `score` on a single, or
   * `scoreK`...`scoreA` on a section.
   *
   * Every write carries ALL FOUR sections, taken from the sheet's own state, and
   * that is not incidental. The sync queue keeps only the LAST callback per key
   * so that typing `100` sends one request rather than three; the key is the
   * CELL, and one cell is one database row. If a write carried only the section
   * just edited, typing K and then T would let T's queued write replace K's and
   * K would be dropped before it was ever sent. The backend upsert REPLACES the
   * row rather than merging into it, which is the same reasoning from the other
   * end: a partial payload there would null the sections it omitted.
   */
  const writeCell = (studentId, assessment, section, raw) => {
    const parsed = parseMark(raw, assessment);
    // `undefined` is "not a mark, ignore it" — deliberately NOT collapsed into
    // `null`, which is "the teacher emptied this cell" and erases it.
    if (parsed === undefined) return;
    /* A TYPED STATUS IS NOT A STATUS ANY MORE. `parseMark` still answers `M` and
     * `EX` — it is shared with the importer and the tests — but in this sheet a
     * status is chosen from the right-click menu and applies to the WHOLE cell,
     * which is a thing one section's input has no business doing. Typing it
     * would also read as a section-level status, and there is no such thing: a
     * student who was away missed the test, not its Thinking section. So the
     * keystroke is dropped and the cell snaps back, exactly as any other
     * non-number does. */
    if (parsed !== null && parsed.status !== "graded") return;

    const key = `${studentId}:${assessment.id}`;
    const current = marks[key];
    let next;

    if (!section) {
      next = parsed;
    } else {
      /* A section edit builds on what is already in the cell — except when the
       * cell holds a status, which has no section scores to preserve and is
       * cleared by typing into it. */
      const base = current && current.status === "graded" ? current : BLANK_MARK;
      const merged = { ...BLANK_MARK, ...base, status: "graded", score: null };
      merged[SECTION_SCORE_FIELD[section]] = parsed === null ? null : parsed.score;
      // Every section empty is an empty CELL, not a graded zero: the row goes.
      next = sectionsOf(assessment).some((s) => merged[SECTION_SCORE_FIELD[s]] != null)
        ? merged
        : null;
    }

    setMarks((prev) => {
      const out = { ...prev };
      if (next === null) delete out[key];
      else out[key] = next;
      return out;
    });
    /* Optimistic above, queued below - the number is on screen immediately and
     * the request is the queue's problem. `null` means the teacher emptied the
     * cell - never marked, which is the absence of a row rather than either
     * status, hence a delete. */
    sync.enqueue(key, () => (next === null
      ? api.clearMark(gradebook.id, studentId, assessment.id)
      : api.saveMark(gradebook.id, studentId, assessment.id, next)));
  };

  /** Commit whatever is in the draft for this cell, if the draft is this cell's. */
  const commitDraft = (studentId, assessment, section, dkey) => {
    if (draft?.key !== dkey) return;
    writeCell(studentId, assessment, section, draft.text);
    setDraft(null);
  };

  /** One status for the whole cell, cascading over every section by
   *  construction: it REPLACES the mark rather than annotating it. */
  const setStatus = (studentId, assessment, status) => {
    const key = `${studentId}:${assessment.id}`;
    const next = status === "graded" ? null : statusMark(status);
    setMarks((prev) => {
      const out = { ...prev };
      if (next === null) delete out[key];
      else out[key] = next;
      return out;
    });
    sync.enqueue(key, () => (next === null
      ? api.clearMark(gradebook.id, studentId, assessment.id)
      : api.saveMark(gradebook.id, studentId, assessment.id, next)));
  };

  /**
   * Scroll a cell out from behind the frozen edges.
   *
   * The sheet has a sticky roster column on the left, a sticky Grade column on
   * the right and a sticky header on top, all of which paint OVER the marks.
   * `scrollIntoView({ block: "nearest" })` treats the scrollport as the target
   * and happily stops with the cell tucked under one of them, so arrowing left
   * along a row walked the cursor straight behind the student names - focused,
   * being typed into, and invisible.
   *
   * So the visible window is the scrollport MINUS those frozen strips, measured
   * live from the elements themselves rather than from the width constants: the
   * roster changes width with the OEN toggle, and a stale number here would
   * park the cursor just behind the edge it was supposed to clear.
   *
   * Adjusts by the smallest amount that clears the obstruction, on each axis
   * independently, so a purely vertical move never scrolls sideways.
   */
  const revealCell = (el) => {
    const port = scrollRef.current;
    if (!port || !el) return;

    const view = port.getBoundingClientRect();
    const cell = el.getBoundingClientRect();

    const frozenLeft = rosterRef.current?.getBoundingClientRect().width ?? 0;
    // Grade plus the delete rail pinned outside it - both paint over marks.
    const frozenRight = (termRef.current?.getBoundingClientRect().width ?? 0) + W_DEL;
    const frozenTop = headRef.current?.getBoundingClientRect().height ?? 0;

    const left = view.left + frozenLeft;
    const right = view.right - frozenRight;
    const top = view.top + frozenTop;

    if (cell.left < left) port.scrollLeft -= left - cell.left;
    else if (cell.right > right) port.scrollLeft += cell.right - right;

    if (cell.top < top) port.scrollTop -= top - cell.top;
    else if (cell.bottom > view.bottom) port.scrollTop += cell.bottom - view.bottom;
  };

  /**
   * Arrow / Enter navigation, stepping OVER anything that cannot take focus.
   *
   * The derived total columns register no cell, so a plain `col + 1` walked
   * straight into a gap and simply stopped — arrow-right dead-ended on the last
   * section of every split and the teacher had to reach for the mouse to carry
   * on. Continuing in the same direction until a real cell turns up makes
   * left/right land on the next EDITABLE mark, which is the only thing arrow
   * keys are for here.
   *
   * The bounds are what guarantee termination: a run of unfocusable columns at
   * the edge exits the loop rather than scanning forever.
   */
  const move = (e, row, col) => {
    const step =
      e.key === "Enter" || e.key === "ArrowDown" ? [1, 0]
      : e.key === "ArrowUp" ? [-1, 0]
      : e.key === "ArrowRight" ? [0, 1]
      : e.key === "ArrowLeft" ? [0, -1]
      : null;
    if (!step) return;

    let r = row + step[0];
    let c = col + step[1];
    while (
      r >= 0 && r < students.length
      && c >= 0 && c < cols.length
      && !cells.current[`${r}:${c}`]
    ) {
      r += step[0];
      c += step[1];
    }
    const el = cells.current[`${r}:${c}`];
    if (el) {
      e.preventDefault();
      /* `preventScroll` because the browser's own scroll-on-focus does not know
       * about the frozen columns - it stops as soon as the cell is technically
       * inside the scrollport, which is often UNDER the roster or the header. */
      el.focus({ preventScroll: true });
      revealCell(el);
    }
  };

  /* The row's real id is assigned by Postgres, so it cannot be shown before the
   * server confirms it - but once it has, the created row is right there in the
   * response and gets appended straight into the cache. Re-fetching the whole
   * gradebook to learn one row was wasteful AND visibly worse: the refetch flipped
   * `loading`, which swapped the sheet for a spinner and remounted it. */
  const addStudent = async ({ name, oen }) => {
    setAddingStudent(false);
    try {
      const created = await api.createStudent(gradebook.id, { name, oen });
      // Appended last, matching the server's own [displayOrder, createdAt] order.
      onMutate((g) => ({ ...g, students: [...g.students, created] }));
    } catch (err) {
      console.error("Failed to add student", err);
    }
  };

  /**
   * Persist one edited roster field.
   *
   * Optimistic through `onMutate` so the cell does not flicker back to its old
   * value while the request is in flight, then reloaded on failure so the sheet
   * never keeps showing an edit the server rejected.
   */
  const saveStudentField = async (student, field, raw) => {
    const value = raw.trim();
    // A student with no name is not a row anyone can use; an empty OEN is a
    // real value meaning "this student does not have one".
    if (field === "name" && !value) return;
    const next = field === "oen" ? (value || null) : value;
    if (next === (student[field] ?? (field === "oen" ? null : ""))) return;

    onMutate((g) => ({
      ...g,
      students: g.students.map((s) => (s.id === student.id ? { ...s, [field]: next } : s)),
    }));
    try {
      await api.updateStudent(gradebook.id, student.id, { [field]: next });
    } catch (err) {
      console.error(`Failed to save student ${field}`, err);
      onReload();
    }
  };

  /* Unlike a gradebook or an assessment, this is a hard delete - the cascade
   * takes the student's marks with them and there is no soft-deleted row left to
   * restore. The type-the-name modal is the gate; this just performs it. */
  const removeStudent = async () => {
    const { id } = deletingStudent; // captured before the modal closes
    setDeletingStudent(null);
    /* Drop any unsent marks for this student first: once the row is gone those
     * writes can only 404, and the sheet would report a save error for a cell
     * that no longer exists. */
    sync.cancel((key) => key.startsWith(`${id}:`));
    try {
      await api.deleteStudent(gradebook.id, id);
      setMarks((prev) => {
        const next = {};
        for (const [key, value] of Object.entries(prev)) {
          if (!key.startsWith(`${id}:`)) next[key] = value;
        }
        return next;
      });
      onMutate((g) => ({ ...g, students: g.students.filter((s) => s.id !== id) }));
    } catch (err) {
      // Only here is a full reload right: the sheet and the server now disagree
      // about whether this student exists, and the server is the authority.
      console.error("Failed to delete student", err);
      onReload();
    }
  };

  /** Class average for a whole assessment, as a percent. Part-marked and excused
   *  cells drop out on their own — `markPercent` returns null for both. */
  const columnAverage = (a) => {
    const vals = students.map((s) => markPercent(marks[`${s.id}:${a.id}`], a)).filter((p) => p != null);
    return vals.length ? vals.reduce((x, y) => x + y, 0) / vals.length : null;
  };

  /**
   * Class average for ONE section of a split, as a percent of that section's max.
   *
   * This is the cheapest form of the insight the whole K/T/C/A feature exists
   * for — "the class is weak on Thinking" is visible in the footer without
   * opening anything. It is deliberately NOT derivable from `columnAverage`,
   * which refuses to score a part-marked assessment at all: a section that IS
   * fully marked has a perfectly good average even while its neighbours are
   * still being marked.
   *
   * Excused drops out (no evidence to average), missing counts as the zero it
   * is, and an unmarked section is simply absent rather than a zero — the same
   * three rules `markPercent` applies one level up.
   */
  const sectionAverage = (a, section) => {
    const max = Number(a[SECTION_MAX_FIELD[section]]);
    if (!(max > 0)) return null;
    const vals = [];
    for (const s of students) {
      const m = marks[`${s.id}:${a.id}`];
      if (!m || m.status === "excused") continue;
      if (m.status === "missing") { vals.push(0); continue; }
      const score = m[SECTION_SCORE_FIELD[section]];
      if (score != null) vals.push((Number(score) / max) * 100);
    }
    return vals.length ? vals.reduce((x, y) => x + y, 0) / vals.length : null;
  };

  /**
   * Backing + glow for one mark cell.
   *
   * LINES ONLY, and no alternating banding underneath. The tinted wash down the
   * cursor's row and column is gone: it was the same yellow that flagged a
   * cell's own state, so a cell could not say "look at me" and "the cursor is
   * here" at once without the two blurring together. The edges alone locate the
   * cursor perfectly well.
   */
  const surface = (row, col) => {
    const r = row === cursor.row;
    const c = col === cursor.col;
    return r && c ? CROSSHAIR_BOTH : c ? CROSSHAIR_COL : r ? CROSSHAIR_ROW : "";
  };

  /* A new assessment defaults to today, but today has to land inside the range
   * being viewed or the column is filtered straight back out of the sheet and
   * the create looks like it silently failed. */
  const defaultDate = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (range.from && today < range.from) return range.from;
    if (range.to && today > range.to) return range.to;
    return today;
  }, [range]);

  const peeked = peek && columns.find((c) => c.id === peek.id);

  const assessmentsHref = `/gradebooks/${gradebook.id}/assessments`;
  /* Clicking a column opens its own row on the Assessments page rather than
   * dropping the teacher at the top of a list to go hunting. */
  const editHref = (a) => `${assessmentsHref}?focus=${a.id}`;
  const weightText = (a) => (weightOf[a.id] ? `${weightOf[a.id].toFixed(1)}%` : "—");

  /** Hover card anchor, off the header's own rect. */
  const openPeek = (a) => (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPeek({
      id: a.id,
      left: Math.min(r.left, window.innerWidth - POPOVER_W - 16),
      top: r.bottom,
    });
  };
  const closePeek = (a) => () => setPeek((v) => (v?.id === a.id ? null : v));

  /* The sheet is `h-[70dvh]` and the PAGE scrolls.
   *
   * It used to be `h-[calc(100dvh-3.5rem)]` on the container with everything
   * inside sharing that one screen, so the title, the controls and the filter
   * all ate into the grid and the sheet got whatever was left — a different,
   * usually smaller, height on every screen, decided at load. 70% is tall enough
   * to work in and short enough to leave the page's own furniture reachable
   * without scrolling past a full screen of grid. `dvh` rather than `vh` so a
   * phone's collapsing URL bar does not leave a strip of dead space under it. */
  return (
    <PageContainer size="wide" className="flex flex-col">
      {/* Two rows, one idea each. Everything used to share a single line with
          the title, which left the date inputs wedged between two buttons and a
          save indicator — unreadable at any width below a desktop. */}
      <div className="mb-4 shrink-0">
        {/* Row 1 — identity, with save state pinned top-right where it has
            always been. Deliberately unmoved: it is the one thing a teacher
            glances at mid-typing, and it sits in the same spot on every page. */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Link
              to="/gradebooks"
              className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground hover:underline"
            >
              <ArrowLeft size={14} /> Back to Gradebooks
            </Link>
            <div className="mt-1.5 flex flex-wrap items-center gap-3">
              {/* The cover card's title colour, carried onto the sheet - the
                  book a teacher opened and the page they landed on stay one
                  object. Same resolver, so the two can never disagree. */}
              <h1 className={cn("font-display text-3xl leading-tight", titleColorClass(gradebook.titleColor))}>
                {gradebook.subject}
              </h1>
              {/* The same grade stamp as the gradebook's cover card, so the
                  book a teacher opened and the sheet they landed on visibly
                  agree about which class this is. */}
              <span className="inline-flex items-baseline gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3.5 py-1.5 text-brand-700 dark:border-brand-800 dark:bg-brand-900 dark:text-brand-300">
                <span className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-80">Grade</span>
                <span className="font-display text-[24px] font-bold leading-none">{gradebook.grade}</span>
              </span>
            </div>
          </div>
          <SyncIndicator status={sync.status} onRetry={sync.retryFailed} className="mt-2" />
        </div>

        {/* Row 2 — everything that changes what the columns look like. The
            three view switches share one dropdown: they are all answers to
            "what should the sheet show me", and three loose checkboxes were
            more strip than the strip had room for. mt-5, not the tight stack
            the rows used to share: the title needs air before the controls. */}
        <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
            <DropdownMenu>
              {/* Dressed as a select, not a button: it holds settings, and a
                  button-weight control kept reading as an action. */}
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-10 items-center gap-2 rounded-md border border-border bg-card px-3 text-sm text-foreground transition-colors hover:border-primary/60 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  View
                  <ChevronDown size={15} className="text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* `onSelect` is prevented on all three: a teacher setting up
                    their view flips more than one, and a menu that slams shut
                    per click makes that three separate trips. */}
                <DropdownMenuCheckboxItem
                  checked={view.compact}
                  onCheckedChange={() => toggleView("compact")}
                  onSelect={(e) => e.preventDefault()}
                >
                  Compact
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={showOen}
                  onCheckedChange={() => toggleView("showOen")}
                  onSelect={(e) => e.preventDefault()}
                >
                  Show OEN
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={showWeights}
                  onCheckedChange={() => toggleView("showWeights")}
                  onSelect={(e) => e.preventDefault()}
                >
                  Show Weights
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={showAverages}
                  onCheckedChange={() => toggleView("showAverages")}
                  onSelect={(e) => e.preventDefault()}
                >
                  Assessment Averages
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={showGrade}
                  onCheckedChange={() => toggleView("showGrade")}
                  onSelect={(e) => e.preventDefault()}
                >
                  Final Grade
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DateRangeFilter
              range={range}
              onChange={setRange}
              fullRange={fullRange}
              hiddenCount={allAssessments.length - columns.length}
            />
            {/* Default size, matching the Report Folder pages - `sm` read as
                suffocated next to the title it serves. */}
            <Button asChild>
              <Link to={assessmentsHref}>
                <SlidersHorizontal size={15} /> Manage Assessments
              </Link>
            </Button>
        </div>
      </div>

      {/* TWO GUARDS AGAINST SILENT MARK CORRUPTION. Neither is a feature anyone
          added — both are defaults that a grid of <input>s inherits, and both
          overwrite a mark with no undo and nothing on screen saying it happened.

          NO DRAG. Text selected inside an <input> is draggable, and every
          <input> is a valid drop target; `draggable={false}` governs neither.
          A cell auto-selects on keyboard focus, so a selection is always sitting
          there ready to be dragged — one stray mouse movement MOVES a mark out
          of one student's row and into another's. `dragstart` cancels dragging a
          mark out; `drop` cancels anything being dropped in, including text
          dragged from another tab, which `dragstart` cannot reach.

          NO MIDDLE-CLICK PASTE. On X11 (Linux) selecting text populates the
          PRIMARY selection and a middle click pastes it at the pointer, so a
          scroll-wheel click over a cell silently overwrites a mark with whatever
          was last selected. `auxclick` is the event for non-primary buttons;
          `mousedown` is cancelled too because some builds action the paste
          there. This stops the PASTE half only — selection populating PRIMARY is
          an OS behaviour no web page can switch off.

          Handled here rather than per-cell: these events all bubble, so one set
          of handlers covers every cell that exists now or later. */}
      <div
        ref={sheetRef}
        onDragStart={(e) => e.preventDefault()}
        onDrop={(e) => e.preventDefault()}
        onAuxClick={(e) => { if (e.button === 1) e.preventDefault(); }}
        onMouseDownCapture={(e) => { if (e.button === 1) e.preventDefault(); }}
        className="flex h-[70dvh] min-h-0 w-full flex-col overflow-hidden border-2 border-black bg-card dark:border-foreground"
      >
        <div ref={scrollRef} className="gb-sheet min-h-[16rem] flex-1 overflow-auto">
          {/* w-full so the sheet is the width of its container rather than of
              its contents. Every column carries an explicit width except the
              gutter before Grade, which absorbs whatever is left. */}
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead ref={headRef}>
              <tr>
                <th
                  ref={rosterRef}
                  data-frozen="left"
                  className={cn(
                    "sticky left-0 top-0 z-40 border-b border-r border-border p-0 text-left align-bottom",
                    HEAD_FILL,
                  )}
                  style={{ width: rosterW, minWidth: rosterW, height: headH }}
                >
                  {/* Mirrors the band stack of every column header, so the one
                      word "Weight" lands on the same line as all the weight
                      figures and reads as that ROW'S NAME. Repeating "Weight:"
                      in front of every number was labelling the same row once
                      per column. */}
                  <div className="flex h-full flex-col">
                    <div className="flex-1" />
                    {expanded && (
                      <>
                        {showWeights && (
                          <div className="flex h-7 items-center justify-end border-t border-border/70 px-[6px]">
                            <span className="text-[11.5px] font-semibold text-muted-foreground">Weight</span>
                          </div>
                        )}
                        <div className="h-9 border-t border-border/70" />
                      </>
                    )}
                  </div>
                </th>

                {columns.map((a) => {
                  const secs = sectionsOf(a);
                  const title = a.title || "Untitled";

                  /* ── EXPANDED SPLIT ──────────────────────────────────────
                     One <th> spanning its sections plus the total, carrying a
                     title band over the K/T/C/A letters. It uses the SAME
                     H_HEAD as every other header, so a sheet of mixed shapes
                     has one flat header strip and switching modes never changes
                     its height. */
                  if (expanded && secs.length) {
                    return (
                      <th
                        key={a.id}
                        colSpan={secs.length + 1}
                        onMouseEnter={openPeek(a)}
                        onMouseLeave={closePeek(a)}
                        className={cn(
                          "sticky top-0 z-30 border-b border-l-2 border-border p-0 align-bottom",
                          HEAD_FILL,
                        )}
                        style={{ height: headH }}
                      >
                        {/* THREE BANDS, and a single-mark header has the same
                            three at the same heights — that is what makes the
                            header strip read as one row rather than two kinds of
                            column. Title takes whatever is left, weight gets its
                            own band, and the bottom band is DENOMINATORS: one
                            per section here, one lone /max on a single column. */}
                        <Link
                          to={editHref(a)}
                          className="flex h-full flex-col"
                        >
                          <div className="flex-1 px-[4px] pt-2">
                            {/* "Oct 2" over the title, centred. Reuses the
                                sheet's own formatDate so the grid and the hover
                                card can never disagree about how a date reads. */}
                            <span className="block text-center text-[10.5px] font-medium leading-none text-muted-foreground">
                              {formatDate(a.date)}
                            </span>
                            <span className="mt-0.5 block text-center text-[13px] font-medium leading-snug text-foreground">
                              {title}
                            </span>
                          </div>

                          {showWeights && (
                            <div className="flex h-7 items-center justify-center border-t border-border/70">
                              <span className="text-[11.5px] text-muted-foreground">{weightText(a)}</span>
                            </div>
                          )}

                          {/* Labels, not controls, and no section colours: the
                              letters already name themselves, and a coloured
                              rule over each one was decoration the grid had to
                              be read through. */}
                          <div className="flex border-t border-border/70">
                            {secs.map((s) => (
                              <span
                                key={s}
                                title={SECTION_NAME[s]}
                                style={{ width: W_PART_NOW, minWidth: W_PART_NOW }}
                                className="flex h-9 flex-col items-center justify-center gap-0 border-r border-border/70"
                              >
                                <span className="text-[13px] font-semibold leading-none text-foreground">{s}</span>
                                <span className="text-[10.5px] leading-none text-muted-foreground">
                                  /{a[SECTION_MAX_FIELD[s]]}
                                </span>
                              </span>
                            ))}
                            {/* The total's own denominator, so every slot in
                                this band carries one. No Σ — the glyph was what
                                read as clutter, not the number. */}
                            <span
                              style={{ width: W_TOTAL, minWidth: W_TOTAL }}
                              className="flex h-9 items-center justify-center border-l-2 border-border bg-muted-foreground/10"
                            >
                              <span className="text-[10.5px] leading-none text-muted-foreground">
                                /{a.max}
                              </span>
                            </span>
                          </div>
                        </Link>
                      </th>
                    );
                  }

                  /* ── EXPANDED SINGLE ─────────────────────────────────────
                     The same three bands at the same heights, so this header
                     and a split's line up across the strip. */
                  if (expanded) {
                    return (
                      <th
                        key={a.id}
                        onMouseEnter={openPeek(a)}
                        onMouseLeave={closePeek(a)}
                        className={cn(
                          "sticky top-0 z-20 border-b border-l border-border p-0 align-bottom",
                          HEAD_FILL,
                        )}
                        style={{ width: W_MARK, minWidth: W_MARK, height: headH }}
                      >
                        <Link
                          to={editHref(a)}
                          className="flex h-full flex-col"
                        >
                          <div className="flex-1 px-[4px] pt-2">
                            <span className="block text-center text-[10.5px] font-medium leading-none text-muted-foreground">
                              {formatDate(a.date)}
                            </span>
                            <span className="mt-0.5 block text-center text-[13px] font-medium leading-snug text-foreground">
                              {title}
                            </span>
                          </div>

                          {showWeights && (
                            <div className="flex h-7 items-center justify-center border-t border-border/70">
                              <span className="text-[11.5px] text-muted-foreground">{weightText(a)}</span>
                            </div>
                          )}

                          {/* "/10", not "out of 10" — this band is denominators
                              across every column, and the long form only read as
                              prose because it was the one column saying it. */}
                          <div className="flex h-9 items-center justify-center border-t border-border/70">
                            <span className="text-[10.5px] leading-none text-muted-foreground">
                              {a.max ? `/${a.max}` : "—"}
                            </span>
                          </div>
                        </Link>
                      </th>
                    );
                  }

                  /* ── COMPACT ─────────────────────────────────────────────
                     The title runs VERTICALLY, bottom to top, centred in its
                     column. A split is the same thing over a wider column, with
                     its K/T/C/A letters along the foot.

                     This replaced a 45-degree diagonal, and it fixes what the
                     diagonal never could. A diagonal label leans out over its
                     neighbours and exits through the TOP of the header, so its
                     length had to be capped by counting CHARACTERS — and
                     characters are not a width in a proportional face, so
                     "Lab 2 — Friction" fitted while "Vocabulary Quiz 1" of the
                     same count did not. Every column truncated somewhere else.

                     Vertical text is bounded by the header's HEIGHT, which is
                     identical for every column, so `text-overflow: ellipsis` can
                     do the cutting in PIXELS. Uniform by construction, with no
                     character budget to maintain, and it stops leaning over the
                     sticky roster and Grade columns entirely. */
                  const groupW = secs.length ? secs.length * W_PART_NOW : W_MARK;
                  return (
                    <th
                      key={a.id}
                      colSpan={secs.length || 1}
                      onMouseEnter={openPeek(a)}
                      onMouseLeave={closePeek(a)}
                      className={cn(
                        "sticky top-0 z-20 border-b border-l border-l-muted-foreground/50 p-0 align-bottom",
                        HEAD_FILL,
                      )}
                      style={{ width: groupW, minWidth: groupW, height: headH }}
                    >
                      {/* Bounded box = the ellipsis boundary. `writing-mode`
                          turns the INLINE axis vertical, so the height is the
                          line length and `text-overflow` applies along it;
                          `rotate(180deg)` flips it to read bottom-to-top. */}
                      <Link
                        to={editHref(a)}
                        className="absolute inset-x-0 flex justify-center"
                        style={{ top: 6, bottom: H_STRIP + 4 }}
                      >
                        <span
                          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                          className={cn(
                            "max-h-full overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-medium leading-none",
                            peek?.id === a.id ? "text-primary" : "text-foreground",
                          )}
                        >
                          {title}
                        </span>
                      </Link>

                      {/* Reserved on EVERY column, empty on a single, so all the
                          vertical titles start at one height and the strip reads
                          as one straight line instead of stepping around
                          whichever columns have letters beneath them. */}
                      <div
                        className="absolute bottom-0 left-0 flex border-t border-border/70"
                        style={{ height: H_STRIP, width: groupW }}
                      >
                        {secs.map((s) => (
                          <span
                            key={s}
                            title={SECTION_NAME[s]}
                            style={{ width: W_PART_NOW, minWidth: W_PART_NOW }}
                            className={cn(
                              "flex items-center justify-center border-r text-[11px] font-semibold text-foreground",
                              COL_RULE,
                            )}
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </th>
                  );
                })}

                {/* Slack absorber, and the home of the "+". The table is
                    `w-full`, so without one column left free every declared
                    width gets stretched and a 40px section renders at 50. */}
                <th
                  className={cn("sticky top-0 z-20 border-b border-border p-0 align-bottom", HEAD_FILL)}
                  style={{ minWidth: W_PLUS, height: headH }}
                >
                  <div className="flex justify-end pb-2 pr-2">
                    {/* Outlined: an unbordered glyph in an empty gutter reads as
                        decoration rather than a control. */}
                    <button
                      type="button"
                      onClick={() => setAdding(true)}
                      title="Add Assessment"
                      className="flex h-6 w-6 items-center justify-center border border-border bg-card text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </th>

                {showGrade && (
                  <th
                    ref={termRef}
                    data-frozen="right"
                    className={cn(
                      "sticky top-0 z-40 border-b border-l-2 border-border px-[2px] align-bottom",
                      HEAD_FILL,
                    )}
                    style={{ width: W_TERM, minWidth: W_TERM, height: headH, right: W_DEL }}
                  >
                    {/* "Grade", not "Term": with the Term 1 / Term 2 control gone
                        there is no term for this to be the grade OF. It is the one
                        number the teacher will put on the report card - their
                        judgement, seeded by the average of whatever the date
                        filter is currently showing. */}
                    <span className="mb-1 block text-[12.5px] font-semibold text-foreground">Grade</span>
                  </th>
                )}

                {/* The delete rail's slot in the header - empty, but it has to
                    exist or Grade's own stickiness would leave a hole here. */}
                <th
                  className={cn("sticky right-0 top-0 z-40 border-b border-border p-0 align-bottom", HEAD_FILL)}
                  style={{ width: W_DEL, minWidth: W_DEL, height: headH }}
                />
              </tr>
            </thead>

            <tbody>
              {students.map((student, row) => {
                const sm = marksOf(student.id);
                /* The Grade column: calculated, never stored, never editable. */
                const grade = weightedAverage(columns, sm);
                const rowActive = row === cursor.row;

                return (
                  /* Fixed height, and `align-top` on the cells below, so a row is
                     the size of its contents in BOTH modes. Without it the table
                     hands leftover container height to the rows, and compact —
                     which has no horizontal scrollbar eating space — came out
                     taller per row than expanded from identical cells. */
                  <tr key={student.id} style={{ height: 24 }}>
                    <td
                      data-frozen="left"
                      className={cn(
                        /* OPAQUE, unconditionally. This was `rowActive ? tint :
                           bg-muted/60`, and removing the crosshair tint left the
                           active row's roster cell with no background at all —
                           the marks scrolling underneath showed straight through
                           it. An alpha cannot be used on anything sticky. */
                        "sticky left-0 z-20 border-b border-r border-border bg-card px-[6px] py-0 align-top",
                        rowActive && CROSSHAIR_ROW,
                      )}
                      style={{ width: rosterW, minWidth: rosterW }}
                    >
                      {/* Name and OEN are CELLS, not labels with an edit button
                          behind them. This is a spreadsheet: you click the thing
                          and type. They use the same draft-on-focus,
                          commit-on-blur pattern as a mark, so a half-typed value
                          is never parsed or saved. */}
                      <span className="flex items-center gap-1.5 leading-6">
                        {showOen && (
                          <OenCell
                            student={student}
                            onCommit={(v) => saveStudentField(student, "oen", v)}
                          />
                        )}
                        {/* An EXPLICIT width, not flex-1. A flex child reports
                            its full text as its intrinsic width, which is exactly
                            what let the table grow the column around a long name
                            instead of clipping it. */}
                        <StudentField
                          value={student.name}
                          placeholder="Student name"
                          onCommit={(v) => saveStudentField(student, "name", v)}
                          className="shrink-0 text-foreground"
                          style={{ width: W_NAME_TEXT, fontSize: SHEET_TEXT_PX }}
                          ariaLabel={`Name for ${student.name}`}
                        />
                      </span>
                    </td>

                    {cols.map((c, col) => {
                      const { a, kind, section } = c;
                      const key = `${student.id}:${a.id}`;
                      const mark = marks[key];
                      const status = mark && mark.status !== "graded" ? mark.status : null;

                      const openMenu = (e) => {
                        e.preventDefault();
                        setMenu({ x: e.clientX, y: e.clientY, studentId: student.id, a });
                      };
                      /* Spread onto every cell alongside onContextMenu, so the
                       * status menu has a mouse route and a touch route. */
                      const press = longPress(({ x, y }) => (
                        setMenu({ x, y, studentId: student.id, a })
                      ));

                      /* ── Total — derived, inert, skipped by the keyboard ──
                         It registers no cell in `cells`, which is what makes
                         `move()` step over it: marking a row runs
                         K → T → C → A → the next assessment's K, and a
                         read-only stop in that path is a keystroke wasted on
                         every student in the class. */
                      if (kind === "total") {
                        const done = status ? 0 : marked(mark, a);
                        const need = sectionsOf(a).length;
                        return (
                          <td
                            key={`${a.id}-total`}
                            onContextMenu={openMenu}
                            {...press}
                            className={cn(
                              "border-b border-l-2 border-r border-border p-0 text-center align-top",
                              COL_RULE,
                              "bg-muted-foreground/[0.07]",
                              surface(row, col),
                            )}
                            style={{ width: W_TOTAL, minWidth: W_TOTAL }}
                            title={
                              status ? formatMark(mark, a)
                                : done > 0 && done < need ? `${done} of ${need} sections marked`
                                  : undefined
                            }
                          >
                            {/* A part-marked total shows as an ordinary number.
                                The blank section cells to its left are the only
                                signal needed, and they are what the teacher is
                                looking at anyway while marking. The RULE is
                                unchanged and lives in `markPercent`: a
                                part-marked assessment still drops out of the
                                weighted average rather than scoring low. */}
                            <span className={cn(
                              "block h-6 text-[14px] font-semibold leading-6",
                              status ? "text-muted-foreground" : "text-foreground",
                            )}>
                              {formatMark(mark, a) ?? ""}
                            </span>
                          </td>
                        );
                      }

                      /* ── An editable cell: a single mark, or one section ── */
                      const isPart = kind === "part";
                      const value = isPart ? mark?.[SECTION_SCORE_FIELD[section]] : mark?.score;
                      const dkey = isPart ? `${key}:${section}` : key;
                      const editing = draft?.key === dkey;
                      /* A score above the max is SAVED, not refused - 34/30 is a
                         real bonus mark. It is also what a typo looks like, and
                         the denominator lives up in the header, so it is called
                         out here rather than silently accepted. */
                      const warning = isPart
                        ? sectionWarning(value, a[SECTION_MAX_FIELD[section]])
                        : markWarning(mark, a);

                      return (
                        <td
                          key={isPart ? `${a.id}-${section}` : a.id}
                          onContextMenu={openMenu}
                          {...press}
                          className={cn(
                            "relative border-b border-r border-border bg-card p-0 align-top",
                            COL_RULE,
                            surface(row, col),
                          )}
                          style={isPart
                            ? { width: W_PART_NOW, minWidth: W_PART_NOW }
                            : { width: W_MARK, minWidth: W_MARK }}
                        >
                          <input
                            ref={(el) => { cells.current[`${row}:${col}`] = el; }}
                            /* While the cell has focus it shows EXACTLY what was
                               typed. Only once editing ends is the value parsed
                               and re-rendered in its stored form — which, on a
                               split's section, is an en dash whenever the cell
                               carries a status: the status names itself ONCE, in
                               the total. Printing "Ex" across five cells of one
                               row would read as five separate decisions taken
                               section by section. */
                            value={editing ? draft.text : (formatMark(mark, a, section) ?? "")}
                            readOnly={!!status}
                            onChange={(e) => setDraft({ key: dkey, text: e.target.value })}
                            onMouseDown={() => { viaPointer.current = true; }}
                            onFocus={(e) => {
                              setCursor({ row, col });
                              /* A STATUS CELL TAKES NO DRAFT. This is a real bug
                                 and not a tidy-up: statuses are chosen from the
                                 menu now, so `parseMark` no longer round-trips
                                 the text a status cell displays. It used to take
                                 an empty draft, and since blur COMMITS the
                                 draft, merely clicking a cell reading "Ex" and
                                 clicking away wrote "" over it and deleted the
                                 status outright. No typing required. */
                              if (status) { setDraft(null); viaPointer.current = false; return; }
                              setDraft({ key: dkey, text: formatMark(mark, a, section) ?? "" });
                              // See `viaPointer`: a key selects so the next
                              // keystroke replaces; a click just places a caret.
                              if (!viaPointer.current) e.target.select();
                              viaPointer.current = false;
                            }}
                            onBlur={() => commitDraft(student.id, a, section, dkey)}
                            onKeyDown={(e) => {
                              if (e.key === "Escape") {
                                setDraft(null); // abandon the edit, keep the stored value
                                return;
                              }
                              // Commit before moving: the next cell's focus would
                              // blur this one anyway, but on the last row there is
                              // nowhere to move and the edit would sit uncommitted.
                              if (e.key === "Enter") commitDraft(student.id, a, section, dkey);
                              move(e, row, col);
                            }}
                            spellCheck={false}
                            title={warning ? `${warning} - this mark is above it` : undefined}
                            aria-invalid={warning ? true : undefined}
                            aria-label={
                              isPart
                                ? `${SECTION_NAME[section]} for ${student.name}, ${a.title || "Untitled"}`
                                : `${student.name}, ${a.title || "Untitled"}`
                            }
                            className={cn(
                              INPUT,
                              (value != null || status) && CELL_ENTERED,
                              status && "text-muted-foreground",
                              /* Red wins over the green "has a value" fill: the
                                 point is that this one needs a second look. */
                              warning && "!bg-destructive/15 font-semibold !text-destructive",
                            )}
                          />
                          {/* Only while the cell is focused: that is the moment
                              the number was typed, and a note on every offending
                              cell at once would be a wall of red over the rows
                              beneath. Not a `title` tooltip - those wait a second
                              before appearing, and this has to answer "out of
                              what?" immediately. */}
                          {warning && editing && (
                            <span
                              role="note"
                              className="absolute left-1/2 top-full z-30 mt-0.5 -translate-x-1/2 whitespace-nowrap border border-destructive/40 bg-destructive px-1.5 py-0.5 text-[10px] font-medium text-destructive-foreground shadow-card"
                            >
                              {warning}
                            </span>
                          )}
                        </td>
                      );
                    })}

                    {/* Matches the "+" header column so Grade stays aligned. */}
                    <td
                      className={cn("border-b border-r border-border bg-card", rowActive && CROSSHAIR_ROW)}
                      style={{ minWidth: W_PLUS }}
                      aria-hidden
                    />

                    {showGrade && (
                    <td
                      data-frozen="right"
                      className={cn(
                        /* Opaque for the same reason as the roster: it is sticky,
                           and the marks scroll underneath it. */
                        "sticky z-20 border-b border-l-2 border-border bg-card px-[2px] text-center align-top",
                        rowActive && CROSSHAIR_ROW,
                      )}
                      style={{ width: W_TERM, minWidth: W_TERM, right: W_DEL }}
                    >
                      {/* TWO FIXED-WIDTH HALVES, not one centred string. The
                          letter is 1-2 glyphs ("R" vs "A-" vs "A+"), so centring
                          the whole thing let its width shove the percentage
                          sideways and no two rows lined up. Each half now owns a
                          fixed box and the percentages form a straight column.

                          READ-ONLY, deliberately. It is `weightedAverage()` over
                          the marks in view and nothing about it is stored. The
                          teacher used to be able to type over it and that
                          override was persisted; that was removed so the
                          gradebook can only ever report what the marks say.
                          Professional judgement still happens - on the report
                          card, where it is exercised and exported - but it does
                          not get written back into the marks record.

                          No "N pending" counter: a part-marked assessment drops
                          out of this average, and the blank section cells in the
                          same row are the signal that it did. */}
                      <span
                        className="flex items-center justify-center leading-6"
                        title="Calculated from the marks in view"
                      >
                        {grade == null ? (
                          <span className="text-[14px] font-semibold text-muted-foreground">—</span>
                        ) : (
                          <>
                            <span className="w-[2.5rem] text-right text-[14px] font-semibold text-foreground">
                              {Math.round(grade)}%
                            </span>
                            <span className="px-1 text-[14px] text-muted-foreground">·</span>
                            <span className="w-[1.6rem] text-left text-[14px] font-semibold text-foreground">
                              {percentToLetter(grade)}
                            </span>
                          </>
                        )}
                      </span>
                    </td>
                    )}

                    {/* The delete rail: past EVERYTHING it would destroy, not
                        beside the name where a mis-click was one pixel from an
                        edit. Permanently visible - a control you can only find
                        by sweeping the mouse over rows is one teachers never
                        learn exists - and kept very light so the column reads
                        as structure; it goes solid red under the cursor. */}
                    <td
                      className={cn(
                        "sticky right-0 z-20 border-b border-l border-border bg-card p-0 align-top",
                        rowActive && CROSSHAIR_ROW,
                      )}
                      style={{ width: W_DEL, minWidth: W_DEL }}
                    >
                      <button
                        type="button"
                        onClick={() => setDeletingStudent(student)}
                        title={`Remove ${student.name}`}
                        aria-label={`Remove ${student.name}`}
                        className="flex h-6 w-full items-center justify-center text-muted-foreground/25 transition-colors hover:text-destructive"
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}

              {/* The same affordance as the "+" that adds a column, so adding a
                  row and adding a column are learned once. It replaced a
                  type-here row, which could only ever collect a name and left
                  the OEN with no entry point at all. */}
              <tr>
                <td
                  data-frozen="left"
                  className="sticky left-0 z-20 border-b border-r border-border bg-card px-2 py-1"
                  style={{ width: rosterW, minWidth: rosterW }}
                >
                  <button
                    type="button"
                    onClick={() => setAddingStudent(true)}
                    title="Add Student"
                    className="flex h-6 items-center gap-1.5 border border-border bg-card px-2 text-[12px] text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    <Plus size={13} /> Add Student
                  </button>
                </td>
                {/* Follows the SLOT count, not the assessment count: a split
                    contributes one column per section, plus a total when the
                    sheet is expanded. The extras are the gutter, the delete
                    rail, and Grade when it is shown. */}
                <td className="border-b border-border" colSpan={cols.length + (showGrade ? 3 : 2)} />
              </tr>

              {/* Soaks up whatever container height is left over. Without it the
                  table hands the slack to the last row, so compact — which has
                  no horizontal scrollbar taking space — grew a taller final row
                  than expanded from identical cells. */}
              <tr aria-hidden style={{ height: "100%" }}>
                <td
                  data-frozen="left"
                  className="sticky left-0 z-10 bg-card p-0"
                  style={{ width: rosterW, minWidth: rosterW }}
                />
                <td colSpan={cols.length + (showGrade ? 3 : 2)} className="p-0" />
              </tr>
            </tbody>

            {showAverages && (
            <tfoot>
              <tr>
                <td
                  data-frozen="left"
                  className={cn(
                    "sticky bottom-0 left-0 z-40 border-r border-t border-border px-[6px] py-1 text-[11px] font-semibold text-muted-foreground",
                    HEAD_FILL,
                  )}
                  style={{ width: rosterW, minWidth: rosterW }}
                >
                  Average
                </td>
                {/* EVERY SLOT IS FILLED, sections included. A class average per
                    K/T/C/A — "the class is weak on Thinking" — is the cheapest
                    form of the coverage insight this whole feature exists for,
                    and it costs one more row of numbers nobody has to open
                    anything to read. */}
                {cols.map((c, col) => {
                  const { a, kind, section } = c;
                  const avg = kind === "part" ? sectionAverage(a, section) : columnAverage(a);
                  const width = kind === "part" ? W_PART_NOW : kind === "total" ? W_TOTAL : W_MARK;
                  return (
                    <td
                      key={kind === "part" ? `${a.id}-${section}` : `${a.id}-${kind}`}
                      className={cn(
                        "sticky bottom-0 z-10 border-r border-t border-border px-1 py-1 text-center text-[11px] text-muted-foreground",
                        HEAD_FILL,
                        kind === "total" && "border-l-2 border-l-border",
                        col === cursor.col && CROSSHAIR_COL,
                      )}
                      style={{ width, minWidth: width }}
                    >
                      {/* Carries its "%", because it is one: these are means of
                          percentages sitting directly under a column of RAW
                          scores out of 40. Bare, "78" under a stack of thirties
                          reads as another score. */}
                      {avg == null ? "-" : `${Math.round(avg)}%`}
                    </td>
                  );
                })}
                <td
                  className={cn("sticky bottom-0 z-10 border-r border-t border-border", HEAD_FILL)}
                  aria-hidden
                />
                {showGrade && (
                  <td
                    data-frozen="right"
                    className={cn(
                      "sticky bottom-0 z-40 border-l-2 border-t border-border",
                      HEAD_FILL,
                    )}
                    style={{ width: W_TERM, minWidth: W_TERM, right: W_DEL }}
                    aria-hidden
                  />
                )}
                <td
                  className={cn("sticky bottom-0 right-0 z-40 border-t border-border", HEAD_FILL)}
                  style={{ width: W_DEL, minWidth: W_DEL }}
                  aria-hidden
                />
              </tr>
            </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Insights leaves the sheet entirely, so it does not belong in the
          control strip that acts on the sheet. It sits below the work instead -
          analysis is where a teacher goes when the marking is done - dressed as
          a miniature of what it opens: charts, gently moving, behind the one
          button. Decoration only, so the whole chart layer is aria-hidden and
          the animation stops under prefers-reduced-motion (global.css). */}
      <div className="mt-10 flex shrink-0 justify-center pb-6">
        <Link
          to={`/gradebooks/${gradebook.id}/insights`}
          className="group relative flex h-28 w-full max-w-[440px] items-center justify-center overflow-hidden rounded-xl border border-border bg-card shadow-card transition-colors hover:border-primary/60"
        >
          <div aria-hidden className="absolute inset-0">
            <div className="absolute bottom-0 left-6 flex items-end gap-2">
              {[34, 58, 42, 72].map((h, i) => (
                <span
                  key={i}
                  className="insights-cta-bar w-5 rounded-t-sm bg-primary/15 transition-colors group-hover:bg-primary/30"
                  style={{ height: h, animationDelay: `${i * 0.4}s` }}
                />
              ))}
            </div>
            <div className="absolute bottom-0 right-6 flex items-end gap-2">
              {[64, 40, 78, 52].map((h, i) => (
                <span
                  key={i}
                  className="insights-cta-bar w-5 rounded-t-sm bg-primary/15 transition-colors group-hover:bg-primary/30"
                  style={{ height: h, animationDelay: `${(i + 2) * 0.4}s` }}
                />
              ))}
            </div>
            {/* A rising trend line drifting through the bars, dashes crawling
                forward - the same grammar as the page it advertises. */}
            <svg
              className="absolute inset-x-0 bottom-2 h-20 w-full text-primary/30"
              viewBox="0 0 440 80"
              preserveAspectRatio="none"
            >
              <path
                d="M0 70 C 90 62, 150 56, 220 40 S 370 12, 440 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="6 5"
                className="insights-cta-line"
              />
            </svg>
          </div>
          <span className="relative z-10 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-card transition-transform duration-200 group-hover:scale-105">
            <TrendingUp size={15} /> Insights
          </span>
        </Link>
      </div>

      {/* Not a `title` tooltip: those wait a second before appearing, and this
          has to answer "which column am I in?" the moment the pointer lands. */}
      {peeked && (
        <div
          className="pointer-events-none fixed z-50 border border-border bg-card px-3 py-2 shadow-card"
          style={{ left: peek.left, top: peek.top, width: POPOVER_W }}
        >
          <p className="text-[13px] font-medium leading-snug text-foreground">
            {peeked.title || "Untitled"}
          </p>
          {/* FIXED SLOTS, not a prose line: the eye learns where each fact
              lives and stops re-reading the sentence per column. Same order
              every time - Category, Date, Out of, Weight - then a split's
              section maxes, with an unassessed section shown as a muted 0. */}
          <dl className="mt-1.5 grid grid-cols-[68px_minmax(0,1fr)] gap-y-0.5 text-[11.5px]">
            <dt className="text-muted-foreground">Category</dt>
            <dd className="text-foreground">{categoryOf[peeked.id]?.name ?? "Not counted"}</dd>
            <dt className="text-muted-foreground">Date</dt>
            <dd className="tabular-nums text-foreground">{formatDateLong(peeked.date)}</dd>
            <dt className="text-muted-foreground">Out of</dt>
            <dd className="tabular-nums text-foreground">{peeked.max ?? "—"}</dd>
            <dt className="text-muted-foreground">Weight</dt>
            <dd className="tabular-nums text-foreground">{weightText(peeked)}</dd>
            {sectionsOf(peeked).length > 0 && (
              <>
                <dt className="text-muted-foreground">K/T/C/A</dt>
                <dd className="flex flex-wrap gap-x-2">
                  {SECTIONS.map((s) => {
                    const sectionMax = peeked[SECTION_MAX_FIELD[s]];
                    return (
                      <span key={s} className={sectionMax == null ? "opacity-50" : undefined}>
                        <span className="font-semibold text-foreground">{s}</span>{" "}
                        <span className="text-muted-foreground">{sectionMax ?? 0}</span>
                      </span>
                    );
                  })}
                </dd>
              </>
            )}
          </dl>
        </div>
      )}

      <StatusMenu
        at={menu}
        onClose={() => setMenu(null)}
        onPick={(s) => setStatus(menu.studentId, menu.a, s)}
      />

      <DeleteStudentModal
        student={deletingStudent}
        markCount={
          deletingStudent
            ? Object.keys(marks).filter((k) => k.startsWith(`${deletingStudent.id}:`)).length
            : 0
        }
        onClose={() => setDeletingStudent(null)}
        onConfirm={removeStudent}
      />

      {addingStudent && (
        <NewStudentModal
          onClose={() => setAddingStudent(false)}
          onCreate={addStudent}
        />
      )}

      {adding && (
        <NewAssessmentModal
          categories={gradebook.categories}
          defaultDate={defaultDate}
          onClose={() => setAdding(false)}
          onCreate={async (a) => {
            setAdding(false);
            try {
              const created = await api.createAssessment(gradebook.id, a);
              /* Spliced in by date, not appended: column order IS date order, so
                 a new October column belongs between September and November, not
                 at the far right. Array.sort is stable, so same-day assessments
                 keep insertion order - matching the server's [date, createdAt]. */
              onMutate((g) => ({
                ...g,
                assessments: [...g.assessments, created].sort((x, y) => x.date.localeCompare(y.date)),
              }));
              /* If the new column falls outside the range being viewed, widen the
                 range to include it rather than letting it vanish. You just made
                 this thing; hiding it reads as the create having silently failed.
                 Reachable in practice: a gradebook whose school year is 2025-2026
                 with a quiz dated September 2026 - exactly how this was found. */
              setRange((r) => ({
                from: r.from && created.date < r.from ? created.date : r.from,
                to: r.to && created.date > r.to ? created.date : r.to,
              }));
            } catch (err) {
              console.error("Failed to create assessment", err);
            }
          }}
        />
      )}
    </PageContainer>
  );
}
